import React, { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { TIME, CYCLE_LABELS, DAY, FALLBACKS, INSTRUMENT_TYPE_COLORS, LABELS, MESSAGES } from '../utils/constants'
import {
  buildInstrumentOccurrenceTooltip,
  getInstrumentOccurrenceLabel,
} from '../utils/studyTimelineScheduleDisplay'

const ScheduledView = ({
  scheduledItems,
  cycleRanges,
  selectedDay,
  onSelectDay,
  maxStudyDay = null,
  visitIdToName = {},
  instrumentIdToName = {},
  useAbsoluteStartDate = false,
  absoluteStartDate = null,
}) => {
  const [collapsedDays, setCollapsedDays] = useState(new Set())

  // Only days that have events (visit, instrument, or cycle start/end), capped at study duration
  const daysWithContent = useMemo(() => {
    const days = new Set()
    scheduledItems.forEach(item => {
      if (item.studyDay == null || item.studyDay < 1) return
      if (item.type === 'visit') days.add(item.studyDay)
      // List deploy / repeat days only; open_for visibility is shown in the detail panel.
      if (item.type === 'instrument') days.add(item.studyDay)
    })
    cycleRanges.forEach(range => {
      if (range.startDay != null && range.startDay >= 1) days.add(range.startDay)
      if (range.endDay != null && range.endDay >= 1) days.add(range.endDay)
    })
    let result = Array.from(days).sort((a, b) => a - b)
    if (maxStudyDay != null && maxStudyDay >= 1) {
      result = result.filter(d => d <= maxStudyDay)
    }
    return result
  }, [scheduledItems, cycleRanges, maxStudyDay])

  // For each day: visits (dark blue), instruments (light blue), then cycle rows (light grey)
  // Cycle: "Start: Cycle X" on start day, "End: Cycle X" on end day; ongoing cycle name when day has visit/instrument and is within cycle
  const daySections = useMemo(() => {
    const tooltipOptions = { visitIdToName, instrumentIdToName, useAbsoluteStartDate, absoluteStartDate }
    return daysWithContent.map(day => {
      const visits = scheduledItems.filter(i => i.studyDay === day && i.type === 'visit')
      const instruments = scheduledItems.filter(i => i.studyDay === day && i.type === 'instrument')
      const hasVisitOrInstrument = visits.length > 0 || instruments.length > 0
      const cycleEvents = []
      cycleRanges.forEach(range => {
        const cycleName = range.cycleName || FALLBACKS.CYCLE
        if (day === range.startDay) {
          cycleEvents.push({
            key: `cycle-start-${range.cycleId}`,
            type: 'cycle',
            time: TIME.ALL_DAY,
            label: `${CYCLE_LABELS.START_PREFIX}${cycleName}`,
          })
        } else if (day === range.endDay) {
          cycleEvents.push({
            key: `cycle-end-${range.cycleId}`,
            type: 'cycle',
            time: TIME.ALL_DAY,
            label: `${CYCLE_LABELS.END_PREFIX}${cycleName}`,
          })
        } else if (hasVisitOrInstrument && day > range.startDay && day < range.endDay) {
          cycleEvents.push({
            key: `cycle-ongoing-${range.cycleId}-${day}`,
            type: 'cycle',
            time: TIME.ALL_DAY,
            label: cycleName,
          })
        }
      })

      const events = [
        ...visits.map(item => ({
          key: item.id,
          type: 'visit',
          time: item.window ? `${item.window}` : TIME.DEFAULT_VISIT_TIME,
          label: item.label || FALLBACKS.EMPTY_DASH,
        })),
        ...instruments.map(item => {
          const instrumentType = (item.instrumentData?.type || '').toUpperCase()
          return {
            key: item.id,
            type: 'instrument',
            instrumentType: instrumentType || null,
            time: item.window || TIME.DEFAULT_INSTRUMENT_TIME,
            label: getInstrumentOccurrenceLabel(item, FALLBACKS.EMPTY_DASH),
            tooltip: buildInstrumentOccurrenceTooltip(item, tooltipOptions),
          }
        }),
        ...cycleEvents,
      ]

      return { day, events }
    })
  }, [
    scheduledItems,
    cycleRanges,
    daysWithContent,
    visitIdToName,
    instrumentIdToName,
    useAbsoluteStartDate,
    absoluteStartDate,
  ])

  // Keep selected day in the list when filters change (e.g. instrument filter)
  useEffect(() => {
    if (daysWithContent.length > 0 && !daysWithContent.includes(selectedDay)) {
      onSelectDay(daysWithContent[0])
    }
  }, [daysWithContent, selectedDay, onSelectDay])

  const toggleDay = (e, day) => {
    e.stopPropagation()
    setCollapsedDays(prev => {
      const next = new Set(prev)
      if (next.has(day)) next.delete(day)
      else next.add(day)
      return next
    })
  }

  return (
    <div className='study-timeline-scheduled'>
      {daySections.map(({ day, events }) => {
        const isSelected = day === selectedDay
        const isCollapsed = collapsedDays.has(day)
        return (
          <div
            key={day}
            className={`study-timeline-scheduled-day ${isSelected ? 'selected' : ''}`}
            onClick={() => onSelectDay(day)}
            role='button'
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelectDay(day)
              }
            }}
          >
            <div className='study-timeline-scheduled-day-header'>
              <button
                type='button'
                className='study-timeline-scheduled-day-toggle'
                onClick={e => toggleDay(e, day)}
                aria-expanded={!isCollapsed}
                title={isCollapsed ? LABELS.EXPAND : LABELS.COLLAPSE}
              >
                <i className={`fas fa-caret-${isCollapsed ? 'right' : 'down'}`} />
              </button>
              <span className='study-timeline-scheduled-day-title'>{DAY.SCHEDULED_PREFIX}{day}</span>
            </div>
            {!isCollapsed && (
              <div className='study-timeline-scheduled-events'>
                {events.length === 0 ? (
                  <div className='study-timeline-scheduled-event study-timeline-scheduled-event-empty'>
                    {MESSAGES.NO_EVENTS}
                  </div>
                ) : (
                  events.map(event => {
                    const dotStyle =
                      event.type === 'instrument' &&
                      event.instrumentType &&
                      INSTRUMENT_TYPE_COLORS[event.instrumentType]
                        ? { backgroundColor: INSTRUMENT_TYPE_COLORS[event.instrumentType].border }
                        : undefined
                    return (
                      <div key={event.key} className='study-timeline-scheduled-event'>
                        <span
                          className={`study-timeline-scheduled-dot study-timeline-scheduled-dot-${event.type}`}
                          style={dotStyle}
                        />
                        <span className='study-timeline-scheduled-time'>{event.time}</span>
                        <span
                          className='study-timeline-scheduled-label'
                          title={event.tooltip || undefined}>
                          {event.label}
                        </span>
                      </div>
                    )
                  })
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

ScheduledView.propTypes = {
  maxStudyDay: PropTypes.number,
  scheduledItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      studyDay: PropTypes.number,
      label: PropTypes.string,
      type: PropTypes.string,
      cycleName: PropTypes.string,
      window: PropTypes.string,
    }),
  ).isRequired,
  cycleRanges: PropTypes.arrayOf(
    PropTypes.shape({
      cycleId: PropTypes.string,
      cycleName: PropTypes.string,
      startDay: PropTypes.number,
      endDay: PropTypes.number,
    }),
  ),
  selectedDay: PropTypes.number,
  onSelectDay: PropTypes.func.isRequired,
  visitIdToName: PropTypes.object,
  instrumentIdToName: PropTypes.object,
  useAbsoluteStartDate: PropTypes.bool,
  absoluteStartDate: PropTypes.string,
}

export default ScheduledView
